/**
 * Owner: Masa Bayu
 * File: QxStudioScreen.tsx
 * Date:Time: 2026-10-29 11:55
 * Description: Studio = bar + Write; Import PDF masuk naskhah Write yang sama.
 */

import { Suspense, lazy, useEffect, type ReactElement } from 'react';
import {
  clearQxStudioCommands,
  setQxStudioEmbedded,
} from '@qx/qxw-core';
import { QxStudioBar } from './QxStudioBar';
import { wipeSharedStudioSessions } from './qx-studio-empty-boot';
import { resetQxStudioMode, setQxStudioMode, useQxStudioMode } from './qx-studio-mode';

/**
 * Badan Studio = qxwriter. `styles.css` ialah rantai CSS writer (bukan
 * `writer-globals.css` pelayar standalone 5180).
 */
const QxwriterApp = lazy(async () => {
  await import('@qx/qxwriter-app/styles.css');
  const { ensureQxwDraftFonts } = await import('@qx/qxwriter-app/lib/qx-draft-fonts');
  ensureQxwDraftFonts();
  const { App } = await import('@qx/qxwriter-app/App');
  return { default: App };
});

function StudioLoading(): ReactElement {
  return (
    <section className="login-page" aria-labelledby="qxeditor-handoff-title" aria-busy="true">
      <h2 id="qxeditor-handoff-title">Studio</h2>
      <p className="login-page__lede">Opening Studio…</p>
    </section>
  );
}

/**
 * Setiap lawatan `/author/studio` buang draf Tulis dikongsi dahulu.
 * QXW pada peranti kekal dan boleh dibuka semula untuk disunting.
 */
function useStudioBoot(): void {
  useEffect(() => {
    resetQxStudioMode();
    let cancelled = false;
    void wipeSharedStudioSessions()
      .catch(() => undefined)
      .then(() => {
        if (!cancelled) setQxStudioMode('write');
      });
    return () => {
      cancelled = true;
    };
  }, []);
}

/**
 * D3 · isyarat modul kepada enjin (editor/writer) supaya mereka mendaftarkan
 * arahan + keadaan ke registri `@qx/qxw-core` dan menyembunyikan bar mereka
 * sendiri. Lazy chunk tidak resolve sehingga sekurang-kurangnya satu tick
 * selepas commit, jadi `useEffect` di sini cukup awal untuk memastikan shell
 * nampak `embedded=true` pada render pertamanya.
 */
function useStudioEmbedSignal(): void {
  useEffect(() => {
    setQxStudioEmbedded(true);
    return () => {
      clearQxStudioCommands('editor');
      clearQxStudioCommands('writer');
      setQxStudioEmbedded(false);
    };
  }, []);
}

export function QxStudioScreen({
  onNavigate,
}: {
  readonly onNavigate: (path: string) => void;
}): ReactElement {
  useStudioBoot();
  useStudioEmbedSignal();
  const mode = useQxStudioMode();
  if (mode === null) return <StudioLoading />;
  return (
    <div className="qx-studio" data-qx-studio-mode={mode} data-qx-studio="">
      <QxStudioBar onBack={() => onNavigate('/author/dashboard')} />
      <Suspense fallback={<StudioLoading />}>
        <QxwriterApp />
      </Suspense>
    </div>
  );
}
